import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { TIMEZONE_OPTIONS } from "../constants/timezones";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const formatMinorCurrency = (amountMinor) => {
  const value = Number(amountMinor || 0) / 100;
  return `$${value.toFixed(2)}`;
};

const formatModeLabel = (value) => {
  if (!value) return "Not specified";

  const map = {
    cycle_start: "Cycle start",
    fixed_day_of_month: "Fixed day of month",
    offset_days_from_cycle_start: "Offset from cycle start",
    fixed_amount: "Fixed amount",
    percentage_amount: "Percentage",
    one_time: "One-time",
    recurring: "Recurring",
    automatic_after_days_overdue: "Automatic after overdue threshold",
    manual_by_r1: "Manual owner review",
    hybrid: "Hybrid (automatic reminders + manual suspension)",
    none: "No automatic suspension",
    auto_after_full_payment: "Automatic after full payment",
    approval_required: "Owner approval required",
    automated_platform: "Automated platform enforcement",
    manual_owner_managed: "Manual owner-managed enforcement",
  };

  return map[value] || value.replace(/_/g, " ");
};

const buildReadableContractTerms = (offerData) => {
  const policy = offerData?.contract?.policySnapshot || {};
  const summary = offerData?.contract?.summary || {};
  const reinstatementWindowDays = policy.reinstatementWindowDays || 0;

  const dueDateText = summary.dueDateRuleLabel || formatModeLabel(policy.dueDateRule);
  const lateFeeText = summary.lateFeeLabel || "No late fee details provided.";
  const suspensionText =
    summary.suspensionLabel || "No suspension details provided.";

  const reinstatementText =
    policy.reinstatementMode === "approval_required"
      ? `If your rental is suspended, paying overdue balance does not reactivate access automatically. The owner must manually approve reinstatement, and approval must occur within ${reinstatementWindowDays} day(s) after suspension.`
      : `If your rental is suspended, access can be automatically restored after full overdue payment is confirmed, within ${reinstatementWindowDays} day(s) after suspension.`;

  const lateFeeDetails = policy.lateFeeEnabled
    ? policy.lateFeeType === "percentage_amount"
      ? `${policy.lateFeePercent || 0}% ${formatModeLabel(policy.lateFeeRecurrence)} fee triggered after ${policy.lateFeeTriggerDays || 0} day(s) overdue.`
      : `${formatMinorCurrency(policy.lateFeeAmountMinor)} ${formatModeLabel(policy.lateFeeRecurrence)} fee triggered after ${policy.lateFeeTriggerDays || 0} day(s) overdue.`
    : "Late fees are not enabled for this contract.";

  const suspensionEffects = policy.suspensionEffects || {};
  const effects = [
    suspensionEffects.hidePublicListing ? "Public listing can be hidden" : null,
    suspensionEffects.blockNewBookings ? "New bookings can be blocked" : null,
    suspensionEffects.keepDashboardAccess
      ? "Dashboard access remains available during suspension"
      : "Dashboard access may be restricted during suspension",
    suspensionEffects.cancelFutureBookings
      ? "Future bookings may be canceled"
      : "Future bookings are not auto-canceled",
  ].filter(Boolean);

  return [
    {
      title: "Payment Schedule",
      body: `Rent is ${offerData?.billingCycle || "scheduled"} at ${formatMinorCurrency(offerData?.rentAmount || 0)}. Payment due rule: ${dueDateText}. Grace period: ${policy.gracePeriodDays ?? 0} day(s).`,
    },
    {
      title: "Late Fee Terms",
      body: `${lateFeeText} ${lateFeeDetails}`,
    },
    {
      title: "Suspension Terms",
      body: `${suspensionText} Suspension mode: ${formatModeLabel(policy.suspensionMode)}.${
        effects.length ? ` Operational effects: ${effects.join("; ")}.` : ""
      }`,
    },
    {
      title: "Reinstatement Terms",
      body: reinstatementText,
    },
    {
      title: "Enforcement Method",
      body: `Enforcement mode: ${formatModeLabel(policy.enforcementMode)}. Collection method: ${offerData?.rentCollectionMethod || "online"}.`,
    },
    {
      title: "Termination",
      body: policy.terminationEnabled
        ? `Contract termination can apply after ${policy.terminationTriggerDaysOverdue || 0} day(s) overdue, with a final cure period of ${policy.finalCurePeriodDays || 0} day(s).`
        : "Termination by overdue trigger is not enabled in this policy.",
    },
  ];
};

const BoothRentalSignup = () => {
  const { referralCode } = useParams();
  const [searchParams] = useSearchParams();

  // Loading and validation states
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showFullContract, setShowFullContract] = useState(false);

  // Offer details
  const [offerData, setOfferData] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    businessName: "",
    bio: "",
    specialties: "",
    services: [],
    preferredStartDate: "", // R7 can choose when rent starts
    timezone: "",
    contractTypedName: "",
    consentAccepted: false,
    viewedFullDocument: false,
  });

  // Professional types for services (will fetch from API)
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const readableContractTerms = buildReadableContractTerms(offerData);

  // The renter's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets the renter submit without
  // ever making the choice, and hides a WRONG detection (travel, VPN, mismatched locale)
  // behind a value that looks perfectly plausible. The field starts empty and must be
  // actively chosen (policy 2026-10-03).
  useEffect(() => {
    validateReferralCode();
    fetchProfessionalTypes();

    // Pre-fill email from invitation URL if present
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]);

  const validateReferralCode = async () => {
    try {
      setValidating(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/booth-rental/validate/${referralCode}`,
      );

      if (response.data.valid) {
        setOfferData(response.data.offer);
        setError("");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Invalid or expired referral code. Please contact the salon.",
      );
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const fetchProfessionalTypes = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/professional-types/public`,
      );
      setProfessionalTypes(response.data.professionalTypes || []);
    } catch (err) {
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError("Please enter your first and last name");
      return;
    }

    if (!formData.email || !formData.phone) {
      setError("Please enter your email and phone number");
      return;
    }

    if (!formData.businessName) {
      setError("Please enter your business name");
      return;
    }

    if (formData.services.length === 0) {
      setError("Please select at least one service you offer");
      return;
    }

    if (!formData.preferredStartDate) {
      setError("Please select your preferred start date");
      return;
    }

    if (!formData.timezone) {
      setError("Please choose your timezone.");
      return;
    }

    if (!formData.contractTypedName || formData.contractTypedName.trim().length < 2) {
      setError("Please type your legal name to sign the booth rental contract");
      return;
    }

    if (!formData.viewedFullDocument) {
      setError("Please confirm you reviewed the full contract terms");
      return;
    }

    if (!formData.consentAccepted) {
      setError("Please accept the contract consent terms to continue");
      return;
    }

    // Validate start date is not in the past
    // Parse as local date (not UTC) to avoid timezone offset issues
    const [sy, sm, sd] = formData.preferredStartDate.split('-').map(Number);
    const selectedDate = new Date(sy, sm - 1, sd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setError("Start date cannot be in the past");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        referralCode: referralCode.toUpperCase(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        businessName: formData.businessName.trim(),
        bio: formData.bio.trim(),
        specialties: formData.specialties
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        services: formData.services,
        preferredStartDate: formData.preferredStartDate, // YYYY-MM-DD format
        timezone: formData.timezone,
        contractConsent: {
          typedName: formData.contractTypedName.trim(),
          consentAccepted: true,
          viewedFullDocument: true,
          consentTextVersion:
            offerData?.contract?.consentTextVersion || "BOOTH_RENTAL_CONSENT_v1",
        },
      };

      await axios.post(
        `${API_BASE_URL}/api/booth-rental/signup`,
        payload,
      );
      setSuccess(true);

      // Don't redirect - user needs to check email first
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Signup failed. Please check your information and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || validating) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1e5480 0%, #4a90c6 100%)",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "40px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div
            style={{
              fontSize: "40px",
              marginBottom: "16px",
            }}
          >
            🏪
          </div>
          <div style={{ fontSize: "18px", color: "#374151" }}>
            Validating referral code...
          </div>
        </div>
      </div>
    );
  }

  if (error && !offerData) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1e5480 0%, #4a90c6 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "40px",
            maxWidth: "500px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontSize: "60px", marginBottom: "20px" }}>❌</div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1f2937",
              marginBottom: "12px",
            }}
          >
            Invalid Referral Code
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "#6b7280",
              marginBottom: "24px",
            }}
          >
            {error}
          </p>
          <Link
            to="/login"
            style={{
              display: "inline-block",
              padding: "12px 32px",
              background: "#2d6da3",
              color: "white",
              borderRadius: "8px",
              textDecoration: "none",
              fontWeight: "600",
            }}
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1e5480 0%, #4a90c6 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "40px",
            maxWidth: "500px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontSize: "60px", marginBottom: "20px" }}>📧</div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1f2937",
              marginBottom: "12px",
            }}
          >
            Check Your Email!
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "#6b7280",
              marginBottom: "12px",
            }}
          >
            We've sent a verification link to your email address.
          </p>
          <p
            style={{
              fontSize: "16px",
              color: "#2d6da3",
              fontWeight: "600",
              marginBottom: "24px",
            }}
          >
            Click the link in your email to verify your account, then you can
            login.
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#9ca3af",
              marginBottom: "12px",
            }}
          >
            The verification link expires in 24 hours.
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#9ca3af",
            }}
          >
            Didn't receive the email? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1e5480 0%, #4a90c6 100%)",
        padding: "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        {/* Offer Details Card */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "32px",
            marginBottom: "24px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏪</div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#1f2937",
                marginBottom: "8px",
              }}
            >
              Booth Rental Opportunity
            </h1>
            <p style={{ fontSize: "16px", color: "#6b7280" }}>
              Join {offerData.businessName} as a booth renter
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              padding: "24px",
              background: "#f9fafb",
              borderRadius: "8px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Location
              </div>
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                {offerData.locationName}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {offerData.city}, {offerData.state}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Rent
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#10b981",
                }}
              >
                ${(offerData.rentAmount / 100).toFixed(2)}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {offerData.billingCycle}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Platform Fee
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#3b82f6",
                }}
              >
                ${offerData.platformSubscription?.toFixed(2) || "10.00"}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                per month
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Spaces Available
              </div>
              <div style={{ fontSize: "20px", fontWeight: "700" }}>
                {offerData.seatsRemaining}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                remaining
              </div>
            </div>
          </div>

          {offerData.description && (
            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "#fef3c7",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#92400e",
                }}
              >
                📝 Offer Details
              </div>
              <div style={{ fontSize: "14px", color: "#78350f" }}>
                {offerData.description}
              </div>
            </div>
          )}
        </div>

        {/* Contract Preview + Signature */}
        {offerData.contract && (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "24px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                marginBottom: "12px",
                color: "#1f2937",
              }}
            >
              Booth Rental Contract
            </h2>
            <div
              style={{
                background: "#f9fafb",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "14px",
              }}
            >
              <div style={{ marginBottom: "6px", fontSize: "14px", color: "#374151" }}>
                <strong>Due Date Rule:</strong> {offerData.contract.summary?.dueDateRuleLabel}
              </div>
              <div style={{ marginBottom: "6px", fontSize: "14px", color: "#374151" }}>
                <strong>Late Fee:</strong> {offerData.contract.summary?.lateFeeLabel}
              </div>
              <div style={{ fontSize: "14px", color: "#374151" }}>
                <strong>Suspension:</strong> {offerData.contract.summary?.suspensionLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowFullContract((prev) => !prev)}
              style={{
                border: "1px solid #cbd5e1",
                background: "#fff",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: "600",
                marginBottom: "14px",
              }}
            >
              {showFullContract ? "Hide Full Contract Terms" : "View Full Contract Terms"}
            </button>

            {showFullContract && (
              <div
                style={{
                  background: "#fff",
                  color: "#0f172a",
                  padding: "0",
                  borderRadius: "10px",
                  border: "1px solid #dbe3ef",
                  fontSize: "14px",
                  lineHeight: 1.6,
                  marginBottom: "14px",
                }}
              >
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                    Booth Rental Contract Terms
                  </div>
                  <div style={{ fontSize: "12px", color: "#475569", marginTop: "4px" }}>
                    These terms govern payment, suspension, and reinstatement for this booth rental offer.
                  </div>
                </div>

                <div style={{ padding: "12px 16px" }}>
                  {readableContractTerms.map((term, index) => (
                    <div
                      key={term.title}
                      style={{
                        marginBottom: index === readableContractTerms.length - 1 ? "0" : "12px",
                        paddingBottom:
                          index === readableContractTerms.length - 1 ? "0" : "12px",
                        borderBottom:
                          index === readableContractTerms.length - 1
                            ? "none"
                            : "1px solid #f1f5f9",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#1e3a8a", marginBottom: "4px" }}>
                        {index + 1}. {term.title}
                      </div>
                      <div style={{ color: "#334155" }}>{term.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#374151",
                }}
              >
                Typed Signature (Legal Name) *
              </label>
              <input
                type="text"
                name="contractTypedName"
                value={formData.contractTypedName}
                onChange={handleInputChange}
                placeholder="Type your full legal name"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <label style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
              <input
                type="checkbox"
                name="viewedFullDocument"
                checked={formData.viewedFullDocument}
                onChange={handleInputChange}
              />
              <span style={{ fontSize: "14px", color: "#374151" }}>
                I reviewed the full contract terms shown above.
              </span>
            </label>

            <label style={{ display: "flex", gap: "8px" }}>
              <input
                type="checkbox"
                name="consentAccepted"
                checked={formData.consentAccepted}
                onChange={handleInputChange}
              />
              <span style={{ fontSize: "14px", color: "#374151" }}>
                I consent to electronic signature and agree to this contract.
              </span>
            </label>
          </div>
        )}

        {/* Signup Form */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <h2
            style={{
              fontSize: "22px",
              fontWeight: "700",
              marginBottom: "24px",
              color: "#1f2937",
            }}
          >
            Create Your Account
          </h2>

          {error && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fee2e2",
                color: "#991b1b",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#374151",
                }}
              >
                Personal Information
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#374151",
                }}
              >
                Business Information
              </h3>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Business Name *
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                  placeholder="How you want to be known to clients"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Bio (Optional)
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Tell clients about yourself and your experience"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Specialties (Optional)
                </label>
                <input
                  type="text"
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  placeholder="e.g., Natural Hair, Balayage, Bridal Makeup"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            {/* Start Date Selection */}
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#374151",
                }}
              >
                Rental Start Date *
              </h3>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  When would you like your booth rental to begin? *
                </label>
                <input
                  type="date"
                  name="preferredStartDate"
                  value={formData.preferredStartDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  max={
                    new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split("T")[0]
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
                <div
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    background: "#dbeafe",
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: "#1e40af",
                  }}
                >
                  💡 <strong>Fair Billing:</strong> Your rent will not start
                  until your chosen date. No rent payment is collected during
                  registration. Your billing cycle begins on your selected
                  start date, and payment requests are sent once your rental is
                  activated. Your profile will become visible to customers only
                  after all visibility requirements are met.
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Your Timezone *
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    background: "white",
                  }}
                >
                  <option value="">Select your timezone...</option>
                  {TIMEZONE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Services Offered */}
            <div style={{ marginBottom: "32px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: "#374151",
                }}
              >
                Services You Offer *
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px",
                }}
              >
                {professionalTypes.map((type) => (
                  <label
                    key={type.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px",
                      border: formData.services.includes(type.value)
                        ? "2px solid #3b82f6"
                        : "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: "pointer",
                      background: formData.services.includes(type.value)
                        ? "#eff6ff"
                        : "white",
                      transition: "all 0.2s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.services.includes(type.value)}
                      onChange={() => handleServiceToggle(type.value)}
                      style={{
                        marginRight: "8px",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      {type.emoji && (
                        <span style={{ marginRight: "6px" }}>{type.emoji}</span>
                      )}
                      {type.label || type.value}
                    </span>
                  </label>
                ))}
              </div>

              {formData.services.length === 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#ef4444",
                  }}
                >
                  Please select at least one service
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "14px",
                background: submitting ? "#9ca3af" : "#2d6da3",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.background = "#1e5480";
              }}
              onMouseLeave={(e) => {
                if (!submitting) e.currentTarget.style.background = "#2d6da3";
              }}
            >
              {submitting ? "Creating Account..." : "Complete Signup"}
            </button>
          </form>

          <div
            style={{
              marginTop: "24px",
              textAlign: "center",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            Already have an account?{" "}
            <Link
              to="/login"
              style={{
                color: "#2d6da3",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothRentalSignup;
